import _ from '@/common/helpers';
import ConditionOperators from '@/components/query/spec/condition-operators';
import CriteriaCondition from '@/components/query/spec/criteria-condition';
import FILTER_TYPES from '@/components/query/filter-types';
import formatters from '@/common/format';
import listSummary from '@/common/helpers/list-summary';
import shortid from 'shortid';

export class NormalizedCondition {
    static PathDelimiter = '/';

    static SummaryBlockTypes = {
        icon: 'icon',
        or: 'or',
        strong: 'strong',
    };

    /**
     * Normalize a single condition
     * @param {CriteriaCondition} condition
     * @param {boolean} newlyAdded
     * @param {boolean} summary
     * @param {{}} definition
     */
    constructor(condition, {
        newlyAdded = false,
        summary = false,
        definition = null,
    } = {}) {
        this.id = shortid.generate();
        this.isActivated = newlyAdded;
        this.isSummary = summary;
        this.definition = definition;
        if (condition) {
            if (condition.isNegative()) {
                this.isNegative = true;
                this.normalize(condition.condition);
            } else {
                this.normalize(condition);
            }
        }
    }

    /**
     * Normalize a condition
     * @param {CriteriaCondition} condition
     */
    normalize(condition) {
        this.data = condition;
        if (condition.isRandom()) {
            this.isRandom = true;
        } else if (condition.isDisjunction()) {
            Reflect.deleteProperty(this, 'isNegative');
            this.isComplex = true;
            const [firstCondition] = condition.conditions;
            this.fullPath = firstCondition.getPath();
            this.resolveComplexCondition(condition.conditions);
        } else if (condition.isValueList() || condition.isComparison() || condition.isContains()) {
            this.isComplex = true;
            this.fullPath = condition.getPath();
            if (condition.isContains()) {
                this.nodes = [];
            }
            this.resolveComplexCondition([condition]);
        } else if (condition.isUnknown()) {
            // top level empty check with optional negation,
            // this can only occur on a text filter with no values with `unknown` enabled
            this.isUnknown = true;
            this.isNegativeUnknown = this.isNegative;
            // delete negative flag on origin to prevent false setup for value list
            Reflect.deleteProperty(this, 'isNegative');
            this.fullPath = condition.getPath();
        } else {
            this.fullPath = condition.getPath();
        }
    }

    /**
     * Resolve a list of conditions into properties at the root level of this
     * condition. A complex condition can only exist for a `text` filter currently.
     * @param {array} conditions
     */
    resolveComplexCondition(conditions) {
        conditions.forEach((node) => {
            const isNegative = node.isNegative();
            let target = node;
            if (isNegative) {
                target = node.condition;
            }
            const storeNode = () => {
                if (this.isContains && !this.nodes.includes(target.path)) {
                    this.nodes.push(target.path);
                    this.innerPath = target.path;
                }
            };
            if (target.isDisjunction()) {
                this.resolveComplexCondition(target.conditions);
            } else if (target.isUnknown()) {
                this.isUnknown = true;
                this.isNegativeUnknown = isNegative;
                storeNode();
            } else if (target.isComparison()) {
                this.isComparison = true;
                this.comparePath = target.comparePath;
                this.isNegativeComparison = this.isNegative ? true : isNegative;
                storeNode();
            } else if (target.isValueList()) {
                this.isValueList = true;
                this.listId = target.listId;
                this.isNegative = this.isNegative ? true : isNegative;
                storeNode();
            } else if (target.isContains()) {
                this.isContains = true;
                this.nodes.push(target.path);
                this.resolveComplexCondition([target.condition]);
            } else {
                this.isNegativeList = isNegative;
                this.listData = target;
                storeNode();
            }
        });
    }

    /**
     * Return the path of the normalized condition if present, otherwise
     * return null;
     * @return {null|string}
     */
    getPath() {
        if (this.fullPath !== undefined) {
            return this.fullPath;
        }
        return null;
    }

    /**
     * Return an array of strings representing the lineage from the path provided.
     * Strips the path delimiter from the start and end of the path.
     * @param path
     * @return {string[]}
     */
    static getLineageFromPath(path) {
        return path.replace(/^\/|\/$/g, '')
            .split(NormalizedCondition.PathDelimiter)
            .map(_.unCamelCase);
    }

    /**
     * Generate a summary of the normalized condition and return
     * the condition text itself and the lineage of the field.
     * @return {array}
     */
    getSummary(fieldProvider) {
        const path = this.getPath();
        const data = this.getDataSummary(fieldProvider);
        let prefix = [];
        if (this.isRandom) {
            prefix = ['Special', 'Random chance'];
        } else if (path !== null) {
            prefix = NormalizedCondition.getLineageFromPath(path);
        }
        if (prefix.length) {
            prefix = [{
                separate: NormalizedCondition.SummaryBlockTypes.icon,
                data: prefix,
            }];
        }
        return [...prefix, ...data];
    }

    /**
     * Generate a summary of all data within the condition and return
     * it in an array of strings used for displaying in the interface.
     * @param {FieldProvider} fieldProvider
     * @return {string[]}
     */
    getDataSummary(fieldProvider) {
        // TODO in future merge summary logic for criteria (rules) and filters (grid)
        const emptySummary = [''];
        if (this.isSummary) {
            return emptySummary;
        }
        if (this.isRandom) {
            if (this.data.value !== null) {
                return [`is ${this.data.value}%`];
            }
        } else {
            // complex conditions that stem from a text filter
            if (this.isComparison) {
                return [
                    {
                        wrap: NormalizedCondition.SummaryBlockTypes.strong,
                        data: `is ${this.isNegativeComparison ? 'not ' : ''}same as`,
                    },
                    {
                        separate: NormalizedCondition.SummaryBlockTypes.icon,
                        data: NormalizedCondition.getLineageFromPath(this.comparePath),
                    },
                ];
            }
            let parts = [];
            if (this.isUnknown) {
                parts.push(this.isNegativeUnknown ? 'is not empty' : 'is empty');
            }
            if (this.listData) {
                const list = listSummary(this.listData.values);
                if (list) {
                    parts.push(`is ${this.isNegativeList ? 'not ' : ''}in ${list}`);
                }
            }
            if (this.isValueList) {
                parts.push(`is ${this.isNegative ? 'not ' : ''}in list ${this.listId}`);
            }
            if (parts.length > 1) {
                parts = [{
                    separate: NormalizedCondition.SummaryBlockTypes.or,
                    data: parts,
                }];
            }
            if (parts.length) {
                return parts;
            }
            // simple conditions
            const {type} = fieldProvider.resolveFieldFromCondition(this);
            if (type === null) {
                return emptySummary;
            }
            /**
             * Check whether all numeric/datetime fields are null.
             * @return {boolean}
             */
            const isEmptyRangeBased = () => this.data
                && this.data.min === null
                && this.data.max === null
                && this.data.value === null;
            if (type === FILTER_TYPES.numeric) {
                if (isEmptyRangeBased()) {
                    return emptySummary;
                }
                if (this.data.isRange()) {
                    return [`is between ${formatters.number(this.data.min)} and ${formatters.number(this.data.max)}`];
                }
                if (this.data.op === ConditionOperators.gte) {
                    return [`is greater than or equal to ${formatters.number(this.data.value)}`];
                }
                return [`is lower than or equal to ${formatters.number(this.data.value)}`];
            }
            if (type === FILTER_TYPES.datetime) {
                if (isEmptyRangeBased()) {
                    return emptySummary;
                }
                if (this.data.isRange()) {
                    return [`is between ${formatters.dateLocal(this.data.min)} and ${formatters.dateLocal(this.data.max)}`];
                }
                if (this.data.op === ConditionOperators.gte) {
                    return [`is after ${formatters.dateLocal(this.data.value)}`];
                }
                return [`is before ${formatters.dateLocal(this.data.value)}`];
            }
            if (type === FILTER_TYPES.boolean || type === FILTER_TYPES.enum) {
                if (this.data.value !== null) {
                    return [`is ${this.data.value.toString()}`];
                }
            }
            // common text filter case
            if (this.data.isSimpleList()) {
                const list = listSummary(this.data.values);
                if (list) {
                    return [`is ${this.isNegative ? 'not ' : ''}in ${list}`];
                }
            }
        }
        return emptySummary;
    }

    /**
     * Create a CriteriaCondition from the base object and wrap
     * with a another negative condition if necessary.
     * @param {object} base
     * @param {boolean?} isNegative
     * @return {CriteriaCondition}
     */
    static composeCondition(base, isNegative = false) {
        const condition = new CriteriaCondition(base);
        if (isNegative) {
            return new CriteriaCondition({
                op: ConditionOperators.not,
                condition,
            });
        }
        return condition;
    }

    /**
     * If the current instance is a contains wrap with another condition and
     * set the path to be the `outerPath`.
     * @param {CriteriaCondition} condition
     * @return {CriteriaCondition}
     */
    wrapContains(condition) {
        if (this.isContains) {
            const [, ...parents] = [...this.nodes].reverse();
            let tree = condition;
            parents.forEach((step) => {
                tree = new CriteriaCondition({
                    path: step,
                    condition: tree,
                    op: ConditionOperators.contains,
                });
            });
            return tree;
        }
        return condition;
    }

    /**
     * When we have a `contains` condition the full path is replaced by the inner
     * condition path.
     * @return {string}
     */
    getInnerOrFullPath() {
        return this.isContains ? this.innerPath : this.fullPath;
    }

    /**
     * Reconstruct a CriteriaCondition from the current normalized condition by checking the various
     * flags that are set.
     * @return {CriteriaCondition}
     */
    reconstructCondition() {
        if (this.isRandom) {
            return NormalizedCondition.composeCondition({
                op: ConditionOperators.random,
                value: this.data.value,
            });
        }
        if (this.isComplex) {
            if (this.isComparison) {
                return this.wrapContains(NormalizedCondition.composeCondition({
                    op: ConditionOperators.same,
                    comparePath: this.comparePath,
                    path: this.getInnerOrFullPath(),
                }, this.isNegativeComparison));
            }
            const conditions = [];
            if (this.isUnknown) {
                conditions.push(NormalizedCondition.composeCondition({
                    op: ConditionOperators.equals,
                    path: this.getInnerOrFullPath(),
                    value: null,
                }, this.isNegativeUnknown));
            }
            if (this.isValueList) {
                conditions.push(NormalizedCondition.composeCondition({
                    op: ConditionOperators.inList,
                    path: this.getInnerOrFullPath(),
                    listId: this.listId,
                }, this.isNegative));
            } else if (this.listData
                && (this.listData.values === null || this.listData.values.length)) {
                conditions.push(NormalizedCondition.composeCondition({
                    op: ConditionOperators.in,
                    path: this.getInnerOrFullPath(),
                    values: this.listData.values,
                }, this.isNegativeList));
            }
            // there's more than a single condition
            // thus we return a disjunction
            if (conditions.length > 1) {
                return this.wrapContains(new CriteriaCondition({
                    op: ConditionOperators.or,
                    conditions,
                }));
            }
            // otherwise we return the only condition within
            if (conditions.length) {
                const [condition] = conditions;
                return this.wrapContains(condition);
            }
        }
        return this.wrapContains(NormalizedCondition.composeCondition(this.data, this.isNegative));
    }

    /**
     * Get the default criteria structure for the field definition provided. This is usually used
     * after selecting a field from the criteria-input search.
     * @param {{}} resolvedField
     * @param {{}} definition
     * @return {NormalizedCondition}
     */
    static getConditionFromField(resolvedField, definition) {
        const nodes = NormalizedCondition.resolveNodeTree(resolvedField);
        let criteria;
        if (nodes.length > 1) {
            // requires contains wrappers, reverse and build from bottom of tree
            const [last, ...parents] = nodes.reverse();
            let tree = {path: last};
            parents.forEach((step) => {
                const base = {
                    path: step,
                    condition: {},
                    op: ConditionOperators.contains,
                };
                base.condition = tree;
                tree = base;
            });
            criteria = tree;
        } else {
            const [path] = nodes;
            criteria = {path};
        }
        return new NormalizedCondition(
            new CriteriaCondition(criteria),
            {
                definition,
                newlyAdded: true,
            },
        );
    }

    /**
     * Resolve the node tree of a field and split on array types that require the use
     * of the `contains` operator.
     * @param {[]} steps
     * @param {{}} definition
     * @return {string[]}
     */
    static resolveNodeTree({steps = [], definition} = {}) {
        let previousWasArray = false;
        const nodes = steps.reduce((accumulator, {def, step}) => {
            // check if the step is flagged as an array or injected as `Array` in the spec
            const isArray = (def.type && def.type === Array) || def.$array;
            if (!accumulator.length || previousWasArray) {
                accumulator.push(step);
            } else {
                const pos = accumulator.length - 1;
                accumulator[pos] = `${accumulator[pos]}${step}`;
            }
            previousWasArray = isArray;
            return accumulator;
        }, []);
        if (definition.$array) {
            // array type like `invoiceIds` that requires
            // an ending delimiter
            nodes.push(NormalizedCondition.PathDelimiter);
        }
        return nodes;
    }

    /**
     * Unflag activation and generate a new ID after properties were modified by a filter.
     * This forces the UI to re-render the condition.
     */
    postUpdate() {
        this.isActivated = false;
        this.id = shortid.generate();
    }
}

/**
 * Normalizes the content of criteria into a set of nodes
 * with flattened conditions that expose properties at the root of the object.
 * @prop {CriteriaCondition} criteria
 * @prop {FieldProvider} fieldProvider
 * @prop {NormalizedCondition[]} nodes
 */
export default class NormalizedCriteria {
    constructor(criteria, fieldProvider) {
        this.criteria = new CriteriaCondition(criteria || {});
        this.fieldProvider = fieldProvider;
        this.nodes = [];
        if (criteria) {
            this.normalize();
        }
    }

    normalize() {
        let nodes = [this.criteria];
        // only unions require sub parsing of conditions, consider
        // everything else to be a single condition (e.g. `or` on same path)
        if (this.criteria.isUnion() && this.criteria.hasConditions()) {
            nodes = this.criteria.conditions;
        }
        nodes.forEach(n => this.parseNode(n));
    }

    /**
     * Parse condition node and extra information from it regardless of depth.
     * @param {CriteriaCondition} node
     */
    parseNode(node) {
        this.nodes.push(new NormalizedCondition(node));
    }

    /**
     * Get a copy of the conditions found in the criteria set.
     * @return {NormalizedCondition[]}
     */
    getConditions() {
        return this.nodes;
    }

    /**
     * Check whether the criteria set has any conditions defined.
     * @return {boolean}
     */
    get hasConditions() {
        return this.nodes.length > 0;
    }

    /**
     * Update the condition node at the provided index.
     * @param {number} index
     * @param {NormalizedCondition} condition
     */
    updateAtIndex(index, condition) {
        condition.postUpdate();
        this.nodes.splice(index, 1, condition);
    }

    /**
     * Remove a condition at the provided index.
     * @param {number} index
     */
    removeAtIndex(index) {
        this.nodes.splice(index, 1);
    }

    /**
     * Add a field to the normalized criteria and trigger its activation in the UI.
     * If already present simply activate it.
     * @param {string} field
     * @param {string} precursor
     * @param {{}} definition
     */
    addField({field, precursor, definition}) {
        const fullPath = `${precursor}${NormalizedCondition.PathDelimiter}${field}${definition.$array ? NormalizedCondition.PathDelimiter : ''}`;
        // check if a node with same path exists
        const existingNode = this.nodes.find(node => node.fullPath === fullPath);
        if (existingNode) {
            // open existing condition
            existingNode.isActivated = true;
        } else {
            // otherwise add one and flag accordingly
            const resolvedField = this.fieldProvider.resolveFieldFromPath(fullPath);
            this.nodes.push(NormalizedCondition.getConditionFromField(resolvedField, definition));
        }
    }

    /**
     * Reconstruct the criteria object from the normalized nodes.
     * @return {CriteriaCondition}
     */
    reconstructCriteria() {
        if (this.nodes.length > 1) {
            return new CriteriaCondition({
                op: ConditionOperators.and,
                conditions: this.nodes.map(n => n.reconstructCondition()),
            });
        }
        const [node] = this.nodes;
        return node.reconstructCondition();
    }
}
